// Shared Near You styles (app tokens). One module so the cards, forms,
// and page shell can't drift apart.

// Full-page layout — renders in .shell-main alongside HomeView/LogView
// so the SideNav (desktop) + BottomNav (mobile) stay visible. Near You
// is a browsing section, not a modal, so no backdrop and no bottom
// sheet — it owns the main column like every other tab.
export const page = {
  padding: '16px 18px 40px',
  animation: 'at-fade 0.2s ease-out',
};
export const pageInner = {
  maxWidth: 640,
  margin: '0 auto',
};
export const header = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
};
export const eyebrow = {
  fontSize: 11,
  letterSpacing: '0.22em',
  textTransform: 'uppercase',
  color: 'var(--cyan)',
};
// Every tap target here is at least 44 px: Near You is used on a phone,
// often one-handed in a store or at a swap.
export const closeBtn = {
  background: 'none',
  border: 'none',
  color: 'var(--text-muted)',
  fontSize: 18,
  cursor: 'pointer',
  padding: 4,
  minWidth: 44,
  minHeight: 44,
};
export const card = {
  borderRadius: 16,
  background: 'var(--surface)',
  border: '1px solid var(--surface-border)',
  overflow: 'hidden',
};
export const cardBody = { padding: 14 };
// Photo box — a natural photo shape (4:3), not a wide letterbox strip.
// aspect-ratio drives the height from the card width; the cap keeps it
// sane on the wide desktop card. cover center-crops any source photo.
// Shared by every Near You image (cards + the upload preview).
export const bannerBox = {
  width: '100%',
  aspectRatio: '4 / 3',
  maxHeight: 320,
};
// No-photo placeholder is a slim accent strip — a full 4:3 gradient with
// a giant initial wasted the whole screen on stores without a photo. A
// real uploaded photo still gets the 4:3 bannerBox.
export const bannerPlaceholder = {
  width: '100%',
  height: 64,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background:
    'radial-gradient(120% 160% at 30% 20%, rgba(56,189,248,0.30), rgba(14,116,144,0.12) 55%, rgba(2,32,54,0.04))',
  borderBottom: '1px solid var(--surface-border)',
};
export const logoBadge = {
  width: 40,
  height: 40,
  borderRadius: 10,
  objectFit: 'cover',
  flex: '0 0 auto',
  background: 'var(--surface-2, rgba(127,127,127,.1))',
  border: '1px solid var(--surface-border)',
};
// Identity row for a directory listing: the logo IS the recognition cue
// a keeper scans for, so it leads the card at a size you can actually
// read a wordmark in. `contain` never crops a logo; the padded plate
// keeps a transparent PNG legible on the dark surface.
// LANDSCAPE, not square. Almost no local fish store has a square logo
// version — the asset they own is a horizontal wordmark, and forcing one
// into a square plate renders it a few pixels tall and unreadable. The
// plate stays a FIXED size so the name/rating column starts at the same
// x on every card (a ragged left edge is what makes a list unscannable),
// and `contain` fits whatever shape arrives inside it: a 4:1 wordmark
// fills the width, a square badge centres at full height.
// No plate, and not square. Two things were wrong with boxing the logo:
// almost no local fish store owns a square version of its mark (what
// they have is a horizontal wordmark, which a square renders a few
// pixels tall), and the plate itself was visible chrome that competed
// with the card border while the logo floated small inside it.
//
// So the mark sits free in a fixed-width gutter: `contain` at 44px tall,
// left-aligned, up to 116px wide. The GUTTER is what stays fixed, not the
// image, so the name column still starts at the same x on every card — a
// ragged left edge is what makes a directory unscannable — while a 4:1
// wordmark gets more than twice the width the square plate allowed. The
// radius is on the image itself, so a logo with a white background reads
// as a deliberate rounded tile rather than a raw rectangle.
export const logoMark = {
  width: 116,
  height: 44,
  objectFit: 'contain',
  objectPosition: 'left center',
  borderRadius: 8,
  flex: '0 0 auto',
};
// A bare letter needs something to sit in, but it keeps the same 116px
// gutter so it lines up with the logos above and below it.
export const logoFallback = {
  width: 116,
  height: 44,
  flex: '0 0 auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
};
export const logoFallbackInner = {
  width: 44,
  height: 44,
  borderRadius: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 21,
  fontWeight: 700,
  color: 'var(--cyan)',
  fontFamily: 'var(--font-heading, inherit)',
  background: 'var(--surface-2, rgba(255,255,255,.06))',
  border: '1px solid var(--surface-border)',
};
// "Why this store fits YOUR tank" — rankPlaces already computes these
// reasons to order the list, but the card never showed them, so the
// ranking looked arbitrary.
export const matchLine = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
  marginTop: 10,
};
export const matchChip = {
  fontSize: 13,
  fontWeight: 650,
  padding: '4px 9px',
  borderRadius: 7,
  background: 'color-mix(in srgb, var(--cyan) 14%, transparent)',
  color: 'var(--cyan)',
  border: '1px solid color-mix(in srgb, var(--cyan) 30%, transparent)',
};
export const moreBtn = {
  minHeight: 44,
  fontSize: 13,
  fontWeight: 600,
  padding: '4px 9px',
  borderRadius: 7,
  background: 'none',
  color: 'var(--text-dim)',
  border: '1px dashed var(--surface-border)',
  cursor: 'pointer',
};
export const bannerInitial = {
  fontSize: 26.5,
  fontWeight: 700,
  color: 'var(--cyan)',
  opacity: 0.85,
  fontFamily: 'var(--font-heading, inherit)',
};
// A store's own logo shown in the no-photo banner strip instead of a
// generic initial — far more recognisable. `contain` keeps a wordmark or
// square badge un-cropped; height fits inside the 64px strip.
export const bannerLogo = {
  height: 46,
  maxWidth: '70%',
  objectFit: 'contain',
  borderRadius: 8,
};
export const hoursRow = (clickable) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  gap: 8,
  marginTop: clickable ? 0 : 10,
  minHeight: clickable ? 44 : undefined,
  padding: 0,
  background: 'none',
  border: 'none',
  cursor: clickable ? 'pointer' : 'default',
  fontSize: 14.5,
  textAlign: 'left',
});
export const dot = (open) => ({
  width: 7,
  height: 7,
  borderRadius: 999,
  background: open ? 'var(--good, #37b26b)' : 'var(--text-dim)',
  flex: '0 0 auto',
});
export const weekPanel = {
  marginTop: 8,
  padding: '10px 12px',
  borderRadius: 10,
  background: 'var(--surface-2, rgba(127,127,127,.08))',
  border: '1px solid var(--surface-border)',
};
export const weekLine = {
  display: 'flex',
  justifyContent: 'space-between',
  fontSize: 14.5,
  padding: '3px 0',
  fontVariantNumeric: 'tabular-nums',
};
export const tag = {
  fontSize: 13,
  fontWeight: 600,
  padding: '4px 9px',
  borderRadius: 7,
  background: 'var(--accent-bg, rgba(127,127,127,.1))',
  color: 'var(--text-muted)',
  border: '1px solid var(--surface-border)',
};
// Primary = a real filled button. Uses --cyan (the app's primary token);
// --accent is not defined in the theme, so the old value rendered a
// transparent, borderless button that read as plain text ("Directions
// doesn't look like a button").
export const btnPrimary = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  minHeight: 44,
  fontSize: 13,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  padding: '9px 14px',
  borderRadius: 10,
  background: 'var(--cyan)',
  color: 'var(--cyan-text)',
  border: '1px solid var(--cyan)',
  fontWeight: 600,
  cursor: 'pointer',
  textDecoration: 'none',
};
export const btnGhost = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  minHeight: 44,
  fontSize: 13,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  padding: '9px 14px',
  borderRadius: 10,
  background: 'transparent',
  color: 'var(--text)',
  border: '1px solid var(--surface-border)',
  cursor: 'pointer',
  textDecoration: 'none',
};
export const fieldInput = {
  width: '100%',
  boxSizing: 'border-box',
  background: 'var(--surface-2, rgba(127,127,127,.1))',
  border: '1px solid var(--surface-border)',
  borderRadius: 10,
  outline: 'none',
  padding: '10px 12px',
  fontSize: 16,
  color: 'var(--text)',
  marginTop: 6,
};
export const timeInput = {
  minHeight: 44,
  background: 'var(--surface-2, rgba(127,127,127,.1))',
  border: '1px solid var(--surface-border)',
  borderRadius: 8,
  padding: '6px 8px',
  fontSize: 15,
  color: 'var(--text)',
  colorScheme: 'dark light',
};
export const linkBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: 44,
  background: 'none',
  border: 'none',
  color: 'var(--cyan)',
  fontSize: 14,
  cursor: 'pointer',
  padding: 0,
};
export const suggestLink = {
  background: 'none',
  border: 'none',
  color: 'var(--text-dim)',
  fontSize: 13.5,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: 44,
  padding: '0 8px 0 0',
  marginTop: 2,
  textDecoration: 'underline',
  textUnderlineOffset: 2,
};
export const sectionLabel = {
  fontSize: 11,
  letterSpacing: '0.22em',
  textTransform: 'uppercase',
  color: 'var(--text-dim)',
  marginTop: 4,
};
export const chip = (active) => ({
  minHeight: 44,
  padding: '8px 14px',
  borderRadius: 999,
  fontSize: 14,
  letterSpacing: '0.06em',
  border: `1px solid ${active ? 'var(--cyan)' : 'var(--surface-border)'}`,
  background: active ? 'var(--accent-bg)' : 'transparent',
  color: active ? 'var(--cyan)' : 'var(--text-muted)',
  fontWeight: active ? 600 : 400,
  cursor: 'pointer',
});
export const miniChip = (active) => ({
  minHeight: 44,
  minWidth: 44,
  padding: '5px 12px',
  borderRadius: 999,
  fontSize: 13,
  letterSpacing: '0.04em',
  border: `1px solid ${active ? 'var(--cyan)' : 'var(--surface-border)'}`,
  background: active ? 'var(--accent-bg)' : 'transparent',
  color: active ? 'var(--cyan)' : 'var(--text-muted)',
  fontWeight: active ? 600 : 400,
  cursor: 'pointer',
});
export const acDropdown = {
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  zIndex: 20,
  marginTop: 4,
  background: 'var(--sheet-bg, var(--surface))',
  border: '1px solid var(--surface-border)',
  borderRadius: 12,
  overflow: 'hidden',
  boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
  maxHeight: 280,
  overflowY: 'auto',
};
export const acItem = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  padding: '9px 12px',
  background: 'none',
  border: 'none',
  borderBottom: '1px solid var(--surface-border)',
  color: 'var(--text)',
  cursor: 'pointer',
};
export const acAttrib = {
  padding: '6px 12px',
  fontSize: 12,
  color: 'var(--text-dim)',
  letterSpacing: '0.03em',
};
